/**
 * MobilePageSkeleton
 * Full-screen shimmer skeleton for native page transitions.
 * Mimics the home screen layout by default (header + KPI strip + card + grid).
 * Uses CSS variables so it respects light/dark theme automatically.
 */
import { motion } from 'framer-motion';
import React from 'react';

/* ── shimmer keyframes injected once ──────────────────────────────── */
const STYLE_ID = 'ggj-skel-anim';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes skel-shimmer {
      0%   { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .ggj-skel {
      background: var(--vscode-bg-elevated);
      border-radius: 8px;
      position: relative;
      overflow: hidden;
    }
    .ggj-skel::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        var(--vscode-bg-hover) 50%,
        transparent 100%
      );
      background-size: 200% 100%;
      animation: skel-shimmer 1.4s ease-in-out infinite;
    }
  `;
  document.head.appendChild(s);
}

type Bone = { w?: string; h?: number; r?: number; mb?: number; mt?: number };

const Bone: React.FC<Bone> = ({ w = '100%', h = 14, r = 8, mb = 0, mt = 0 }) => (
  <div
    className="ggj-skel"
    style={{ width: w, height: h, borderRadius: r, marginBottom: mb, marginTop: mt }}
  />
);

const MobilePageSkeleton: React.FC = () => (
  <motion.div
    initial={{ opacity: 1 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.15 }}
    style={{
      position: 'absolute',
      inset: 0,
      background: 'var(--vscode-bg-primary)',
      zIndex: 50,
      overflowY: 'auto',
    }}
  >
    {/* Header skeleton — mirrors gradient header */}
    <div
      style={{
        background: 'var(--vscode-bg-elevated)',
        paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
        paddingBottom: 22,
        paddingLeft: 20,
        paddingRight: 20,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <Bone w="55%"  h={10} r={5}  mb={8} />
          <Bone w="75%"  h={22} r={8}  mb={6} />
          <Bone w="60%"  h={12} r={5} />
        </div>
        {/* Credits pill + avatar */}
        <Bone w="52px" h={26} r={14} />
        <Bone w="36px" h={36} r={18} />
      </div>
    </div>

    <div style={{ padding: '16px 16px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* KPI strip */}
      <div style={{ display: 'flex', gap: 8 }}>
        {[0,1,2,3].map(i => (
          <div
            key={i}
            className="ggj-skel"
            style={{ flex: 1, height: 64, borderRadius: 14 }}
          />
        ))}
      </div>

      {/* Next Step card */}
      <Bone w="100%" h={80} r={16} />

      {/* Recent activity list */}
      <div
        style={{
          background: 'var(--vscode-bg-elevated)',
          border: '1px solid var(--vscode-border-primary)',
          borderRadius: 16,
          overflow: 'hidden',
          padding: '2px 0',
        }}
      >
        {[0,1,2].map((i) => (
          <div key={i}>
            {i > 0 && <div style={{ height: 1, background: 'var(--vscode-border-divider)', margin: '0 16px' }} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
              <Bone w="36px" h={36} r={10} />
              <div style={{ flex: 1 }}>
                <Bone w="70%" h={13} r={5} mb={6} />
                <Bone w="45%" h={10} r={5} />
              </div>
              <Bone w="24px" h={16} r={4} />
            </div>
          </div>
        ))}
      </div>

      {/* Quick actions label */}
      <Bone w="35%" h={13} r={5} />

      {/* 2×2 grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {[0,1,2,3].map(i => (
          <div
            key={i}
            className="ggj-skel"
            style={{ height: 96, borderRadius: 16 }}
          />
        ))}
      </div>

    </div>
  </motion.div>
);

export default MobilePageSkeleton;
